import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { useApp } from "../context/AppContext";
import Seo from "../components/Seo";
import { FaqSection } from "../components/FaqSection";
import { faqData } from "../data/faqData";
import { grapeVarieties, IMAGES, PRICE_PER_BOTTLE, MIN_BOTTLES } from "../data/mock";
import { createCuvee } from "../services/api";
import { Plus, X, RotateCcw, ArrowRight, Wine, Minus } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const WineConfigurator = () => {
  const { t, lang, addToCart } = useApp();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [grapes, setGrapes] = useState(grapeVarieties);
  const [selected, setSelected] = useState([
    { id: "cabernet", pct: 50 },
    { id: "merlot", pct: 50 },
  ]);
  const [bottles, setBottles] = useState(MIN_BOTTLES);

  useEffect(() => {
    import("../services/api").then(({ getGrapes }) =>
      getGrapes().then((d) => { if (Array.isArray(d) && d.length) setGrapes(d); }).catch(() => {})
    );
  }, []);

  const getGrape = (id) => grapes.find((g) => g.id === id);
  const total = useMemo(() => selected.reduce((s, x) => s + x.pct, 0), [selected]);
  const isBalanced = total === 100;

  const profile = useMemo(() => {
    const keys = ["body", "tannin", "acidity", "fruit", "oak"];
    const res = {};
    keys.forEach((k) => {
      const sum = selected.reduce((s, x) => s + (getGrape(x.id)?.profile[k] || 0) * x.pct, 0);
      res[k] = total > 0 ? Math.round(sum / total) : 0;
    });
    return res;
  }, [selected, total]);

  const addVariety = (id) => {
    if (selected.find((x) => x.id === id)) return;
    setSelected((prev) => [...prev, { id, pct: 10 }]);
  };
  const removeVariety = (id) => setSelected((prev) => prev.filter((x) => x.id !== id));
  const setPct = (id, pct) => setSelected((prev) => prev.map((x) => (x.id === id ? { ...x, pct } : x)));
  const reset = () => setSelected([]);

  const available = grapes.filter((g) => !selected.find((x) => x.id === g.id));
  const price = bottles * PRICE_PER_BOTTLE;

  const proceed = async () => {
    const name = selected.map((x) => getGrape(x.id)?.name.split(" ")[0]).join(" / ");
    const blend = { name: `Cuvée ${name}`, selected, profile, bottles };
    try {
      const saved = await createCuvee(blend);
      localStorage.setItem("moc_blend", JSON.stringify({ ...blend, id: saved.id }));
    } catch {
      localStorage.setItem("moc_blend", JSON.stringify(blend));
    }
    toast({ title: t("common.added"), description: `${bottles} ${t("common.bottles")}` });
    navigate("/label-designer");
  };

  const profileLabels = { body: t("wine.body"), tannin: t("wine.tannin"), acidity: t("wine.acidity"), fruit: t("wine.fruit"), oak: t("wine.oak") };

  return (
    <div className="pt-20 min-h-screen bg-cream">
      <Seo title={t("wine.title")} description={t("wine.sub")} image={IMAGES.pour2} path="/wine-configurator" lang={lang} />
      {/* header */}
      <div className="relative py-16 overflow-hidden bg-ink">
        <img src={IMAGES.pour2} alt="Wein wird zum Verkosten eingeschenkt" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <h1 className="font-serif text-4xl md:text-5xl text-cream">{t("wine.title")}</h1>
          <p className="mt-4 text-cream/75 max-w-2xl mx-auto">{t("wine.sub")}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-14 grid lg:grid-cols-[1fr_400px] gap-10">
        {/* LEFT: builder */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-2xl text-ink">{t("wine.yourBlend")}</h2>
            <button onClick={reset} className="flex items-center gap-1.5 text-sm text-ink/60 hover:text-wine transition-colors"><RotateCcw className="w-4 h-4" /> {t("wine.reset")}</button>
          </div>

          {selected.length === 0 && (
            <div className="bg-white border border-dashed border-wine/30 rounded-2xl p-10 text-center text-ink/50">{t("wine.emptyBlend")}</div>
          )}

          <div className="space-y-4">
            {selected.map((x) => {
              const g = getGrape(x.id);
              return (
                <div key={x.id} className="bg-white rounded-2xl border border-border p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 rounded-full" style={{ background: g.color }} />
                      <div>
                        <p className="font-medium text-ink">{g.name}</p>
                        <p className="text-xs text-ink/50">{g.notes[lang]}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-serif text-xl text-wine w-14 text-right">{x.pct}%</span>
                      <button onClick={() => removeVariety(x.id)} className="text-ink/30 hover:text-destructive transition-colors"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <Slider value={[x.pct]} onValueChange={(v) => setPct(x.id, v[0])} min={10} max={100} step={5} />
                  <p className="text-[11px] text-ink/40 mt-2">{lang === "de" ? "Min. 10% · Schritte von 5%" : "Min. 10% · 5% steps"}</p>
                </div>
              );
            })}
          </div>

          {/* total bar */}
          {selected.length > 0 && (
            <div className={`mt-5 rounded-xl px-5 py-4 flex items-center justify-between border ${isBalanced ? "bg-wine/5 border-wine/30" : "bg-gold/10 border-gold/40"}`}>
              <span className="text-sm font-medium text-ink/70">{t("wine.total")}</span>
              <div className="flex items-center gap-3">
                <span className={`font-serif text-2xl ${isBalanced ? "text-wine" : "text-gold-dark"}`}>{total}%</span>
                <span className="text-xs font-medium text-ink/60">
                  {isBalanced ? t("wine.perfect") : total > 100 ? `${total - 100}% ${t("wine.over")}` : t("wine.balance")}
                </span>
              </div>
            </div>
          )}

          {/* available varieties */}
          {available.length > 0 && (
            <div className="mt-10">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-ink/50 mb-4">{t("wine.addVariety")}</h3>
              <div className="flex flex-wrap gap-2.5">
                {available.map((g) => (
                  <button key={g.id} onClick={() => addVariety(g.id)} className="group flex items-center gap-2 bg-white border border-border rounded-full pl-2 pr-4 py-2 hover:border-wine hover:shadow-md transition-all">
                    <span className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: g.color }}><Plus className="w-3.5 h-3.5 text-white" /></span>
                    <span className="text-sm font-medium text-ink">{g.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: live preview */}
        <div className="lg:sticky lg:top-24 self-start space-y-6">
          {/* blend visual + dynamic 0-100% scale */}
          <div className="bg-white rounded-2xl border border-border p-7">
            <div className="flex items-end justify-center gap-5">
              {/* bottle filling up to total% */}
              <div className="relative">
                <svg viewBox="0 0 200 600" width="104" height="312">
                  <defs>
                    <clipPath id="bottleClip">
                      <path d="M82 6 L118 6 L118 24 L116 30 L122 122 C122 178 172 184 172 240 L172 580 Q172 590 162 590 L38 590 Q28 590 28 580 L28 240 C28 184 78 178 78 122 L84 30 L82 24 Z" />
                    </clipPath>
                  </defs>
                  <g clipPath="url(#bottleClip)">
                    <rect x="0" y="0" width="200" height="600" fill="#F1E9DD" />
                    {(() => {
                      let acc = 0;
                      return selected.map((x) => {
                        const g = getGrape(x.id);
                        const h = total > 0 ? (x.pct / Math.max(total, 100)) * 584 : 0;
                        const y = 590 - acc - h;
                        acc += h;
                        return <rect key={x.id} x="0" y={y} width="200" height={h} fill={g.color} style={{ transition: "all 0.5s ease" }} />;
                      });
                    })()}
                    <rect x="58" y="280" width="9" height="290" rx="4.5" fill="rgba(255,255,255,0.14)" />
                  </g>
                  <path d="M82 6 L118 6 L118 24 L116 30 L122 122 C122 178 172 184 172 240 L172 580 Q172 590 162 590 L38 590 Q28 590 28 580 L28 240 C28 184 78 178 78 122 L84 30 L82 24 Z" fill="none" stroke="#2B1B1D" strokeOpacity="0.14" strokeWidth="2.5" />
                </svg>
              </div>

              {/* vertical 0-100% gauge */}
              <div className="flex items-stretch gap-2" style={{ height: 312 }}>
                <div className="relative w-3 rounded-full bg-cream-dark overflow-hidden">
                  <div className="absolute bottom-0 inset-x-0 transition-all duration-500" style={{ height: `${Math.min(total, 100)}%`, background: isBalanced ? "#5b1a2e" : "#C9A24B" }} />
                  {/* 100% target line */}
                  <div className="absolute top-0 inset-x-0 h-px bg-ink/30" />
                </div>
                <div className="flex flex-col justify-between text-[11px] text-ink/45 py-0.5">
                  <span>100%</span><span>75%</span><span>50%</span><span>25%</span><span>0%</span>
                </div>
              </div>
            </div>

            {/* status */}
            <div className="mt-5 text-center">
              <p className={`font-serif text-2xl ${isBalanced ? "text-wine" : "text-gold-dark"}`}>{total}%</p>
              <p className="text-sm text-ink/55 mt-0.5">
                {selected.length === 0
                  ? (lang === "de" ? "Noch keine Rebsorte" : "No variety yet")
                  : isBalanced
                    ? t("wine.perfect")
                    : total > 100
                      ? (lang === "de" ? `${total - 100}% zu viel` : `${total - 100}% over`)
                      : (lang === "de" ? `Noch ${100 - total}% übrig` : `${100 - total}% remaining`)}
              </p>
              <p className="text-xs text-ink/40 mt-1">{selected.length} {lang === "de" ? "Rebsorten" : "varieties"}</p>
            </div>
          </div>

          {/* flavor profile */}
          <div className="bg-white rounded-2xl border border-border p-7">
            <h3 className="font-serif text-xl text-ink mb-5">{t("wine.profile")}</h3>
            <div className="space-y-4">
              {Object.keys(profileLabels).map((k) => (
                <div key={k}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-ink/70">{profileLabels[k]}</span>
                    <span className="text-wine font-medium">{profile[k]}</span>
                  </div>
                  <div className="h-2 rounded-full bg-cream-dark overflow-hidden">
                    <div className="h-full bg-wine rounded-full transition-all duration-500" style={{ width: `${profile[k]}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* order */}
          <div className="bg-white rounded-2xl border border-border p-7">
            <label className="text-sm font-medium text-ink/70">{t("wine.bottlesLabel")}</label>
            <div className="flex items-center gap-3 mt-3">
              <button onClick={() => setBottles((b) => Math.max(MIN_BOTTLES, b - 1))} className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-cream-dark transition-colors"><Minus className="w-4 h-4" /></button>
              <span className="font-serif text-2xl text-ink w-16 text-center">{bottles}</span>
              <button onClick={() => setBottles((b) => b + 1)} className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-cream-dark transition-colors"><Plus className="w-4 h-4" /></button>
              <span className="text-xs text-ink/40 ml-1">{t("wine.min")}</span>
            </div>
            <div className="flex items-end justify-between mt-6 pt-5 border-t border-border">
              <div>
                <p className="text-xs text-ink/50">{t("common.currency")} {PRICE_PER_BOTTLE.toFixed(2)} {t("wine.pricePer")}</p>
                <p className="font-serif text-3xl text-wine">{t("common.currency")} {price.toFixed(2)}</p>
              </div>
            </div>
            <Button onClick={proceed} disabled={!isBalanced} className="w-full mt-5 h-13 bg-wine hover:bg-wine-dark text-white rounded-full gap-2 h-12 disabled:opacity-40">
              {t("wine.continueLabel")} <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
      <FaqSection faqs={faqData.configurator[lang] || faqData.configurator.de} lang={lang} />
    </div>
  );
};

export default WineConfigurator;
